import type { SessionDto } from "@bafe/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import {
	isBlockedForMailOnlyMember,
	isMailOnlyOrganisationRole,
	WEBMAIL_HOME,
} from "./mail-only-member.js";
import { queryKeys } from "./query";
import { activeMembership } from "./session.js";
import { clearPendingSignIn, useOrganisationStore } from "./stores";

const PUBLIC_AUTH_PATHS = ["/signin", "/register", "/forgot-password", "/reset-password", "/verify-email", "/2fa"] as const;

/** Vite `base` without a trailing slash, e.g. `/platform`. Empty at site root. */
export function appBasename(): string {
	return import.meta.env.BASE_URL.replace(/\/$/, "");
}

/** App routes are served at `/manage`, `/hub`, … Caddy only prefixes static files with `/platform`. */
export function routerBasename(): string | undefined {
	return undefined;
}

/** Strip the Vite base prefix so `/platform/hub` becomes `/hub`. */
export function stripAppBasename(pathname: string): string {
	const base = appBasename();
	if (!base) return pathname;
	if (pathname === base) return "/";
	if (pathname.startsWith(`${base}/`)) return pathname.slice(base.length) || "/";
	return pathname;
}

export function isPublicAuthPath(pathname: string): boolean {
	const path = stripAppBasename(pathname.split("?")[0] ?? pathname);
	return PUBLIC_AUTH_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** Only same-origin paths are honoured, so `?next=` cannot send anyone away. */
export function safeNextPath(next: string | null): string {
	if (!next) return "/";
	if (!next.startsWith("/") || next.startsWith("//")) return "/";
	const pathOnly = next.split("?")[0] ?? next;
	if (isPublicAuthPath(pathOnly)) return "/";
	if (next.includes("signin")) {
		try {
			if (decodeURIComponent(next).includes("/signin")) return "/";
		} catch {
			return "/";
		}
	}
	return next;
}

/** Router-relative target for `<Navigate to={...} />`. */
export function signInNavigateTarget(pathname: string, search: string): string {
	if (isPublicAuthPath(pathname)) return "/signin";
	const next = safeNextPath(`${stripAppBasename(pathname)}${search}`);
	if (next === "/") return "/signin";
	return `/signin?next=${encodeURIComponent(next)}`;
}

/** Full browser URL for `window.location.assign`. Returns null when already on sign-in. */
export function signInHref(pathname: string, search: string): string | null {
	if (isPublicAuthPath(pathname)) return null;
	const next = safeNextPath(`${stripAppBasename(pathname)}${search}`);
	if (next === "/") return "/signin";
	return `/signin?next=${encodeURIComponent(next)}`;
}

export function destinationAfterSignIn(session: SessionDto, next: string): string {
	const organisationId =
		session.activeOrganisationId ?? session.memberships[0]?.organisationId ?? null;
	const membership = activeMembership(session, organisationId);
	const resolved = safeNextPath(next);

	if (membership && isMailOnlyOrganisationRole(membership.role)) {
		if (resolved === "/" || isBlockedForMailOnlyMember(resolved)) {
			return WEBMAIL_HOME;
		}
	}

	return resolved;
}

export function useCompleteSignIn() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);

	return useCallback(
		(session: SessionDto, next: string) => {
			clearPendingSignIn();
			setOrganisationId(
				session.activeOrganisationId ?? session.memberships[0]?.organisationId ?? null,
			);
			queryClient.setQueryData(queryKeys.session, session);
			const target = destinationAfterSignIn(session, next);
			if (target.startsWith("/webmail")) {
				window.location.assign(target);
				return;
			}
			void navigate(target, { replace: true });
		},
		[navigate, queryClient, setOrganisationId],
	);
}
